import angular from 'angular'

import { QuickKeyNodeType } from '../qk.constants'
import { getProductDetails } from '../qk-layout'
import template from './vd-quick-key.html'

/**
 * Directive for rendering a quick key.
 *
 * @module vendQuickKeys.vdQuickKey
 */
angular.module('vendQuickKeys').directive('vdQuickKey', function () {
  const DRAG_EVENTS = [
    'dragstart',
    'dragenter',
    'dragleave',
    'dragover',
    'drop',
  ]
  const ALL_EVENTS = ['click', 'mouseenter', 'mouseleave', 'mousedown'].concat(
    DRAG_EVENTS
  )

  return {
    restrict: 'E',
    scope: {
      quickKey: '=',
      pageIndex: '=',
      layout: '=',
      readOnly: '@?',
    },
    template,
    bindToController: true,
    controllerAs: 'vdQuickKeyCtrl',
    controller(
      this: $TSFixMe,
      $scope,
      $element,
      key,
      qkModals,
      vdProductImagePathFilter,
      vdFeatureManager
    ) {
      this._vdProductImagePathFilter = vdProductImagePathFilter
      this._vdFeatureManager = vdFeatureManager

      this._getPosition = function () {
        return {
          position: this.quickKey.node.position,
          pageIndex: this.pageIndex,
        }
      }

      this._quickKeySelected = function () {
        switch (this.quickKey.type) {
          case QuickKeyNodeType.FOLDER:
            $scope.$applyAsync(() => {
              this.layout.folder = this.quickKey
            })
            break

          case QuickKeyNodeType.BLANK:
            $scope.$applyAsync(() => {
              this.layout.editTarget = this.quickKey
            })
            break

          case QuickKeyNodeType.QUICK_KEY:
            if (this.layout.editable) {
              qkModals.editQuicKey(this.layout, this.quickKey)
            } else {
              $scope.$applyAsync(() => {
                this.layout.selectQuickKey(this.quickKey)
              })
            }
            break
        }
      }

      this._keyboardNavigate = function (direction: $TSFixMe) {
        const fromPosition = this._getPosition()
        $scope.$emit('vd-quick-keys:keyboard-navigate', {
          fromPosition,
          direction,
        })
      }

      this._bindEvents = function () {
        // There is a webkit bug where the combination of :hover selectors and 'drag and drop' creates ghost hover
        // states on other unconnected quick keys.
        $element.on('mouseenter', (event: $TSFixMe) =>
          $element.addClass('vd-quick-key--hover')
        )
        $element.on('mouseleave', (event: $TSFixMe) =>
          $element.removeClass('vd-quick-key--hover')
        )

        $element.on('mousedown', (event: $TSFixMe) => {
          $scope.$emit(
            'vd-quick-keys:mousedown-at-position',
            this._getPosition()
          )
        })

        $element.on('click', () => {
          $scope.$emit('vd-quick-keys:focus-at-position', this._getPosition())
          this._quickKeySelected()
        })

        this._keyEvents = key.on(
          $element,
          {
            ENTER: () => this._quickKeySelected(),
            UP: () => this._keyboardNavigate('UP'),
            DOWN: () => this._keyboardNavigate('DOWN'),
            LEFT: () => this._keyboardNavigate('LEFT'),
            RIGHT: () => this._keyboardNavigate('RIGHT'),
          },
          { preventDefault: true }
        )

        // Drag and Drop
        $element.on(DRAG_EVENTS.join(' '), (event: $TSFixMe) =>
          this._manageDragEvent(event)
        )
      }

      this._manageDragEvent = function (event: $TSFixMe) {
        switch (event.type) {
          case 'dragstart':
            // Setting the effect and data allows the drag to start
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', this.quickKey.node.id)
            if (event.dataTransfer.setDragImage) {
              // setDragImage is not supported in Edge
              event.dataTransfer.setDragImage($element[0], 5, 5)
            }

            $element.removeClass('vd-quick-key--hover')
            $scope.$emit('vd-quick-keys:drag-start', this.quickKey)
            break
          case 'dragenter':
            if (
              this.layout.draggedQuickKey &&
              this.layout.draggedQuickKey.node.id !== this.quickKey.node.id
            ) {
              $element.addClass('vd-quick-key--drag-over')

              if (!this.layout.isValidDropDestination(this.quickKey)) {
                $element.addClass('vd-quick-key--not-allowed')
              }
            }
            break
          case 'dragleave':
            $element.removeClass(
              'vd-quick-key--drag-over vd-quick-key--not-allowed'
            )
            break
          case 'dragover':
            // Cancel the drag over event so the drop event can be triggered
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
            break
          case 'drop': {
            // Cancel the drop event to stop any other event processing from occuring. Without this, FireFox will open
            // a new tab.
            event.preventDefault()

            const eventData = event.dataTransfer.getData('text')

            // Ensure that only quick keys are dropped. In the case of a non quick key being dragged into the grid
            // and then dropped, remove the drag-over selector applied during drag enter.
            if (
              this.layout.draggedQuickKey &&
              eventData !== this.layout.draggedQuickKey.node.id
            ) {
              $element.removeClass('vd-quick-key--drag-over')
              return
            }

            $element.removeClass(
              'vd-quick-key--drag-over vd-quick-key--not-allowed'
            )
            $scope.$emit(
              'vd-quick-keys:drop',
              this.quickKey,
              this._getPosition()
            )
            break
          }
        }
      }

      this._detachEvents = function () {
        $element.off(ALL_EVENTS.join(' '))
        if (this._keyEvents) {
          this._keyEvents.off()
        }
      }

      this._resolveImageUrl = function () {
        if (this.quickKey.viewData.showImage) {
          this.layout.getProduct(this.quickKey).then((product: $TSFixMe) => {
            if (!product) {
              return
            }

            const isChild = getProductDetails(this.quickKey).isChild

            if (isChild) {
              // `isChild` should indicate that the product is a variant and be shown as a variant child. But sometimes thats not correct,
              // so check the product really is a variant before attempting to use a skuImage.
              const isVariantParent = product.has_variants
              const isVariantChild = Boolean(product.variant_parent_id)

              if (isVariantChild || isVariantParent) {
                if (product.skuImages?.length) {
                  this.imageUrl = this._vdProductImagePathFilter(
                    product,
                    'sm',
                    true
                  )
                }
                return
              }
            }

            if (
              product.images?.length ||
              !product.image_url.includes('no-image-white-standard')
            ) {
              this.imageUrl = this._vdProductImagePathFilter(product, 'sm')
            }
          })
        } else {
          this.imageUrl = null
        }
      }

      this.$onInit = function () {
        // Bind showImage behaviour even in read-only mode as necessary for quick key preview when editing
        $scope.$watch('vdQuickKeyCtrl.quickKey.viewData.showImage', () =>
          this._resolveImageUrl()
        )

        const isReadOnly = !angular.isUndefined(this.readOnly)
        if (isReadOnly) {
          $element.addClass('vd-quick-key--read-only')
          // No need to bind events etc if this quick key is in read only mode so return here
          return
        }

        if (this.quickKey.type !== QuickKeyNodeType.BLANK) {
          this._bindEvents()
        } else {
          // For blank keys, bind events only in edit mode
          $scope.$watch(
            'vdQuickKeyCtrl.layout.editable',
            (newVal: $TSFixMe) => {
              if (newVal) {
                this._bindEvents()
              } else {
                this._detachEvents()
                $element.removeAttr('tabindex')
              }
            }
          )
        }
      }
    },
  }
})
